import { useState } from "react"
import { apiFetch } from "../../../utils/apiFetch"

const API_BASE =
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

export default function usePatientFinanceActions({
  selectedPatientId,
  clinicId,
  isReadOnly,
  readOnlyReason,
  loadPatientFinance,
  loadAdvancedFinance,
  loadPatientAllocations,
  loadFinancialTimeline
}) {
  const [applyingPayment, setApplyingPayment] = useState(false)
  const [reversingAllocation, setReversingAllocation] = useState(false)
  const [cancellingPayment, setCancellingPayment] = useState(false)

  const [newPaymentForm, setNewPaymentForm] = useState({
    amount: "",
    method: "CASH",
    notes: "",
    autoAllocate: false,
    accountReceivableId: ""
  })

  const [manualApplyForm, setManualApplyForm] = useState({
    paymentId: "",
    accountReceivableId: "",
    amount: ""
  })

  

  const [manualReceivableForm, setManualReceivableForm] = useState({
    concept: "Saldo anterior",
    totalAmount: "",
    dueDate: "",
    notes: ""
  })

  const [savingManualReceivable, setSavingManualReceivable] = useState(false)
const [savingPayment, setSavingPayment] = useState(false)
  const [savingAndPrinting, setSavingAndPrinting] = useState(false)

  const refreshFinance = async () => {
    if (!selectedPatientId) return
    await loadPatientFinance(selectedPatientId)
    await loadAdvancedFinance(selectedPatientId)
    await loadPatientAllocations(selectedPatientId)
    await loadFinancialTimeline(selectedPatientId)
  }

  const printPaymentReceipt = async (paymentId) => {
    try {
      if (!paymentId) return

      const res = await apiFetch(`${API_BASE}/finance/payment-receipt/${paymentId}`)
      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo generar el recibo.")
        return
      }

      const win = window.open("", "_blank")
      if (!win) {
        alert("No se pudo abrir la ventana de impresión.")
        return
      }

      win.document.write(`
        <html>
        <head>
          <title>Recibo de pago</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 30px; color: #111; }
            h1 { margin-bottom: 0; }
            h3 { margin-top: 24px; margin-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            td, th { border: 1px solid #ccc; padding: 8px; text-align: left; }
            .right { text-align: right; }
          </style>
        </head>
        <body>
          <h1>Recibo de pago</h1>
          <h3>Folio: ${data.id}</h3>
          <p>
            Paciente: ${data.patientName || "Paciente"} <br/>
            Fecha: ${new Date(data.createdAt).toLocaleString("es-MX")} <br/>
            Método: ${data.method || "-"} <br/>
            Monto pagado: $${Number(data.amount || 0).toFixed(2)} <br/>
            Saldo a favor restante: $${Number(data.unallocatedAmount || 0).toFixed(2)} <br/>
            Estado: ${data.status || "-"} <br/>
            Referencia: ${data.referenceType || "-"} <br/>
            Notas: ${data.notes || "-"}
          </p>

          <h3>Aplicaciones del pago</h3>
          <table>
            <tr>
              <th>Concepto</th>
              <th class="right">Monto</th>
            </tr>
            ${(data.allocations || [])
              .map(
                (a) => `
                  <tr>
                    <td>${a.concept || "Cuenta por cobrar"}</td>
                    <td class="right">$${Number(a.amount || 0).toFixed(2)}</td>
                  </tr>
                `
              )
              .join("")}
          </table>

          <script>window.print();</script>
        </body>
        </html>
      `)

      win.document.close()
    } catch {
      alert("Error al generar el recibo.")
    }
  }

  const applyPaymentManually = async () => {
    try {
      if (isReadOnly) {
        alert(readOnlyReason || "Modo solo lectura")
        return
      }

      if (!manualApplyForm.paymentId) {
        alert("Selecciona un pago con saldo disponible.")
        return
      }

      if (!manualApplyForm.accountReceivableId) {
        alert("Selecciona una cuenta por cobrar pendiente.")
        return
      }

      setApplyingPayment(true)

      const payload = {
        paymentId: manualApplyForm.paymentId,
        accountReceivableId: manualApplyForm.accountReceivableId
      }

      if (manualApplyForm.amount) {
        payload.amount = Number(manualApplyForm.amount)
      }

      const res = await apiFetch(`${API_BASE}/finance/manual-apply-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo aplicar el saldo.")
        return
      }

      alert("Saldo aplicado correctamente.")

      setManualApplyForm({
        paymentId: "",
        accountReceivableId: "",
        amount: ""
      })

      await refreshFinance()
    } catch {
      alert("Error al aplicar saldo.")
    } finally {
      setApplyingPayment(false)
    }
  }

  const createManualReceivable = async () => {
    try {
      if (isReadOnly) {
        alert(readOnlyReason || "Modo solo lectura")
        return false
      }

      if (!selectedPatientId) {
        alert("Selecciona un paciente.")
        return false
      }

      if (!clinicId) {
        alert("No se encontró una clínica válida.")
        return false
      }

      const amount = Number(manualReceivableForm.totalAmount || 0)

      if (!Number.isFinite(amount) || amount <= 0) {
        alert("Ingresa un monto válido.")
        return false
      }

      const concept = String(manualReceivableForm.concept || "").trim()

      if (!concept) {
        alert("Ingresa un concepto para la deuda.")
        return false
      }

      setSavingManualReceivable(true)

      const payload = {
        clinicId,
        patientId: selectedPatientId,
        totalAmount: amount,
        concept,
        notes: manualReceivableForm.notes?.trim() || undefined,
        dueDate: manualReceivableForm.dueDate || undefined
      }

      const res = await apiFetch(`${API_BASE}/finance/receivable`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo agregar la deuda.")
        return false
      }

      setManualReceivableForm({
        concept: "Saldo anterior",
        totalAmount: "",
        dueDate: "",
        notes: ""
      })

      await refreshFinance()

      alert("Deuda agregada correctamente.")
      return true
    } catch (error) {
      console.error("Error al agregar deuda manual:", error)
      alert("Error al agregar deuda.")
      return false
    } finally {
      setSavingManualReceivable(false)
    }
  }

  const registerNewPayment = async ({ shouldPrint = false } = {}) => {
    try {
      if (isReadOnly) {
        alert(readOnlyReason || "Modo solo lectura")
        return
      }

      if (!selectedPatientId) {
        alert("No hay un paciente seleccionado.")
        return
      }

      if (!clinicId) {
        alert("No hay una clínica válida seleccionada.")
        return
      }

      if (!newPaymentForm.amount || Number(newPaymentForm.amount) <= 0) {
        alert("Ingresa un monto válido.")
        return
      }

      if (shouldPrint) {
        setSavingAndPrinting(true)
      } else {
        setSavingPayment(true)
      }

      const res = await apiFetch(`${API_BASE}/finance/payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          clinicId,
          patientId: selectedPatientId,
          amount: Number(newPaymentForm.amount),
          method: newPaymentForm.method,
          notes: newPaymentForm.notes || undefined,
          referenceType: "MANUAL"
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo registrar el pago.")
        return
      }

      let autoApplySucceeded = true

      if (
        newPaymentForm.autoAllocate &&
        newPaymentForm.accountReceivableId &&
        data?.id
      ) {
        const allocRes = await apiFetch(`${API_BASE}/finance/manual-apply-payment`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            paymentId: data.id,
            accountReceivableId: newPaymentForm.accountReceivableId
          })
        })

        const allocData = await allocRes.json().catch(() => null)

        if (!allocRes.ok) {
          autoApplySucceeded = false
          alert(
            allocData?.message ||
              "El pago se registró, pero no se pudo aplicar automáticamente."
          )
        }
      }

      setNewPaymentForm({
        amount: "",
        method: "CASH",
        notes: "",
        autoAllocate: false,
        accountReceivableId: ""
      })

      await refreshFinance()

      if (shouldPrint && data?.id) {
        await printPaymentReceipt(data.id)
      }

      if (autoApplySucceeded) {
        alert(
          shouldPrint
            ? "Pago registrado e impresión abierta correctamente."
            : "Pago registrado correctamente."
        )
      }
    } catch {
      alert("Error al registrar el pago.")
    } finally {
      setSavingPayment(false)
      setSavingAndPrinting(false)
    }
  }

  const reverseAllocation = async (allocationId) => {
    try {
      if (isReadOnly) {
        alert(readOnlyReason || "Modo solo lectura")
        return
      }

      if (!allocationId) return

      setReversingAllocation(true)

      const res = await apiFetch(`${API_BASE}/finance/reverse-allocation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ allocationId })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo revertir la asignación.")
        return
      }

      alert("Asignación revertida correctamente.")
      await refreshFinance()
    } catch {
      alert("Error al revertir asignación.")
    } finally {
      setReversingAllocation(false)
    }
  }

  const cancelPayment = async (paymentId) => {
    try {
      if (isReadOnly) {
        alert(readOnlyReason || "Modo solo lectura")
        return
      }

      if (!paymentId) return

      setCancellingPayment(true)

      const res = await apiFetch(`${API_BASE}/finance/cancel-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ paymentId })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo cancelar el pago.")
        return
      }

      alert("Pago cancelado correctamente.")
      await refreshFinance()
    } catch {
      alert("Error al cancelar pago.")
    } finally {
      setCancellingPayment(false)
    }
  }

  return {
    applyingPayment,
    reversingAllocation,
    cancellingPayment,
    newPaymentForm,
    setNewPaymentForm,
    manualApplyForm,
    setManualApplyForm,
    savingPayment,
    savingAndPrinting,
    manualReceivableForm,
    setManualReceivableForm,
    savingManualReceivable,
    createManualReceivable,
    applyPaymentManually,
    registerNewPayment,
    reverseAllocation,
    cancelPayment,
    printPaymentReceipt
  }
}
